import React from 'react';
import { BlogBlock } from '../../types/blog';
import { getBlogStyles } from '../../themes/styles/blog';
import { ColorTheme } from '../../themes/colors';

interface BlockRendererProps {
  blocks: BlogBlock[];
  theme: ColorTheme;
}

const CALLOUT_ICONS: Record<string, string> = {
  tip: '💡',
  warning: '⚠️',
  info: 'ℹ️',
};

function getYouTubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com')) {
      const v = u.searchParams.get('v');
      return v ? `https://www.youtube.com/embed/${v}` : null;
    }
    if (u.hostname === 'youtu.be') {
      return `https://www.youtube.com/embed${u.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
}

const BlockRenderer: React.FC<BlockRendererProps> = ({ blocks, theme }) => {
  const s = getBlogStyles(theme);
  const sorted = [...blocks].sort((a, b) => a.order - b.order);

  return (
    <div style={s.blockContent}>
      {sorted.map((block, i) => {
        switch (block.type) {
          case 'heading': {
            const level = block.level ?? 2;
            if (level === 1) return <h1 key={i} style={s.blockHeading1}>{block.content}</h1>;
            if (level === 3) return <h3 key={i} style={s.blockHeading3}>{block.content}</h3>;
            return <h2 key={i} style={s.blockHeading2}>{block.content}</h2>;
          }

          case 'paragraph':
            return (
              <p
                key={i}
                style={s.blockParagraph}
                dangerouslySetInnerHTML={{ __html: block.content ?? '' }}
              />
            );

          case 'image':
            return (
              <div key={i} style={s.blockImageWrap}>
                <img
                  src={block.url}
                  alt={block.alt ?? block.caption ?? ''}
                  style={s.blockImageImg}
                  loading="lazy"
                  onError={(e) => {
                    const el = e.currentTarget;
                    el.style.display = 'none';
                    const msg = document.createElement('p');
                    msg.textContent = '⚠️ Image could not be loaded. Use a direct image URL (ending in .jpg, .png, .webp).';
                    msg.style.cssText = 'color:#9ca3af;font-size:0.85rem;padding:1rem;border:1px dashed #e5e7eb;border-radius:8px;text-align:center';
                    el.parentNode?.appendChild(msg);
                  }}
                />
                {block.caption && (
                  <p style={s.blockImageCaption}>{block.caption}</p>
                )}
              </div>
            );

          case 'image_grid':
            return (
              <div key={i} style={s.blockImageGrid}>
                {(block.images ?? []).map((img, j) => (
                  <div key={j} style={s.blockImageGridItem}>
                    <img
                      src={img.url}
                      alt={img.alt ?? img.caption ?? ''}
                      style={s.blockImageGridImg}
                      loading="lazy"
                    />
                    {img.caption && (
                      <p style={s.blockImageCaption}>{img.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            );

          case 'bullet_list':
            return (
              <ul key={i} style={s.blockListUl}>
                {(block.items ?? []).map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ul>
            );

          case 'numbered_list':
            return (
              <ol key={i} style={s.blockListOl}>
                {(block.items ?? []).map((item, j) => (
                  <li key={j}>{item}</li>
                ))}
              </ol>
            );

          case 'quote':
            return (
              <blockquote key={i} style={s.blockQuote}>
                {block.content}
                {block.author && (
                  <span style={s.blockQuoteAuthor}>— {block.author}</span>
                )}
              </blockquote>
            );

          case 'callout':
            return (
              <div key={i} style={s.blockCallout}>
                <span style={s.blockCalloutIcon}>
                  {CALLOUT_ICONS[block.variant ?? 'info']}
                </span>
                <span>{block.content}</span>
              </div>
            );

          case 'divider':
            return <hr key={i} style={s.blockDivider} />;

          case 'video_embed': {
            const embedUrl = getYouTubeEmbedUrl(block.url ?? '');
            return (
              <div key={i}>
                <div style={s.blockVideoWrap}>
                  {embedUrl && (
                    <iframe
                      src={embedUrl}
                      style={s.blockVideoIframe}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={block.caption ?? 'Video'}
                    />
                  )}
                </div>
                {block.caption && (
                  <p style={s.blockImageCaption}>{block.caption}</p>
                )}
              </div>
            );
          }

          case 'stat_highlight':
            return (
              <div key={i} style={s.blockStatHighlight}>
                {(block.stats ?? []).map((stat, j) => (
                  <div key={j} style={s.blockStat}>
                    <span style={s.blockStatValue}>{stat.value}</span>
                    <span style={s.blockStatLabel}>{stat.label}</span>
                  </div>
                ))}
              </div>
            );

          case 'recipe_card':
            return (
              <div key={i} style={s.blockRecipeCard}>
                <div>
                  <p style={s.blockRecipeTitle}>Ingredients</p>
                  <ul style={s.blockRecipeList}>
                    {(block.ingredients ?? []).map((ing, j) => (
                      <li key={j}>• {ing}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p style={s.blockRecipeTitle}>Steps</p>
                  <ol style={{ ...s.blockRecipeList, listStyle: 'none', padding: 0 }}>
                    {(block.steps ?? []).map((step, j) => (
                      <li key={j}>{j + 1}. {step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            );

          default:
            return null;
        }
      })}
    </div>
  );
};

export default BlockRenderer;
